import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useRef} from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import {
  doBoxSchemasWithRegistryQuery,
  doContainerSchemasWithRegistryQuery,
  doLocationSchemasWithRegistryQuery,
  doPlateSchemasWithRegistryQuery,
} from 'inventory/useStorableSchemasQuery';
import EditableLookupConfigDisplay from 'lookup-config/EditableLookupConfigDisplay';
import LookupConfigModal from 'lookup-config/LookupConfigModal';
import {
  DEFAULT_INTERMEDIATE_LOOKUP_STEP_TYPES,
  LookupConfigSpec,
  LookupStepType,
} from 'lookup-config/LookupConfigModalConstants';
import {RunLookupConfigModalProps} from 'lookup-config/LookupConfigUtils';
import {isLookupConfigValidForModal} from 'lookup-config/LookupStepInitializerUtils';
import {doAllProcessTaskSchemasForSchemaConfigQuery} from 'processes/modals/utils/ProcessesAllSchemasQueryUtils';
import {sharedStyles} from 'processes/settings/fields/ProcessTaskSchemaFieldConstants';
import {ProcessTaskSchema} from 'processes/settings/ProcessTaskSchemaUtils';
import {doAllResultSchemasQuery} from 'results/useAllResultSchemasQuery';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {doAllSchemaInterfacesQuery} from 'schemas/util/useAllSchemaInterfacesQuery';
import {doTagSchemasWithRegistryQuery} from 'schemas/util/useTagSchemasQuery';
import {isItemLinkFieldType} from 'tag-schema/util/TagSchemaUtil';
import FormGroup from 'ui-library/bs/FormGroup';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_LIGHT, GRAY} from 'ui-library/Colors';
import ErrorMessage from 'ui-library/ErrorMessage';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import InputWrapper from 'ui-library/inputs/InputWrapper';
import legacyRunModal from 'ui-library/legacyRunModal';
import {withLoadingScreen} from 'util/Promises';

export const LOOKUP_HELP_TEXT =
  'Configure a lookup to automatically create responsible parties for a task based on a task field (in addition to the specified default responsible parties).';

interface ResponsiblePartyLookupConfigFieldProps {
  value: LookupConfigSpec | null;
  onChange: (value: LookupConfigSpec | null) => void;
  isBeingCreated: boolean;
  isDisabled?: boolean;
  label: string;
  taskSchema: ProcessTaskSchema | null;
  isLoading: boolean;
  error?: string;
}

async function runLookupConfigModalForResponsibleParty(modalProps: RunLookupConfigModalProps): Promise<void> {
  // Ensure all schemas are loaded
  const supportingData = Promise.all([
    doAllResultSchemasQuery(),
    doBoxSchemasWithRegistryQuery(),
    doContainerSchemasWithRegistryQuery(),
    doLocationSchemasWithRegistryQuery(),
    doPlateSchemasWithRegistryQuery(),
    doTagSchemasWithRegistryQuery(),
    doAllSchemaInterfacesQuery(),
    doAllProcessTaskSchemasForSchemaConfigQuery(),
  ]);
  await withLoadingScreen(supportingData);
  assert(isLookupConfigValidForModal(modalProps), 'Lookup config is not supported in the lookup modal', {
    modalProps,
  });
  legacyRunModal(LookupConfigModal, modalProps);
}

export function ResponsiblePartyLookupFieldContent(
  props: ResponsiblePartyLookupConfigFieldProps
): JSX.Element {
  if (props.isBeingCreated) {
    return (
      <span className={css(sharedStyles.messageText)}>
        Configure the lookup after the task schema is created.
      </span>
    );
  }

  let tooltipTitle = '';
  if (!props.taskSchema!.fields.some((field: SchemaField) => isItemLinkFieldType(field.fieldType))) {
    tooltipTitle = 'Not available if there are no link fields on the task schema';
  }

  const isButtonDisabled = Boolean(props.isDisabled) || Boolean(tooltipTitle);

  return (
    <>
      {props.value != null ? (
        <EditableLookupConfigDisplay
          baseSchema={props.taskSchema}
          lookupConfig={props.value}
          onEdit={() => {
            runLookupConfigModalForResponsibleParty({
              baseSchema: props.taskSchema,
              initialLookupConfig: props.value,
              firstLookupStepTypes: [LookupStepType.SCHEMA_FIELD],
              intermediateLookupStepTypes: DEFAULT_INTERMEDIATE_LOOKUP_STEP_TYPES,
              finalLookupStepTypes: [LookupStepType.AUTHORIZED_USERS, LookupStepType.SAMPLE_OWNERS],
              onRequestSubmit: ([{lookupConfig}]) => {
                props.onChange(lookupConfig);
              },
              submitButtonText: 'Done',
            });
          }}
          onRemove={() => {
            props.onChange(null);
          }}
          lookupType="task to responsible party"
          isDisabled={Boolean(props.isDisabled) || Boolean(tooltipTitle)}
          lookupConfigDescriptionTestAttrs={{
            component: 'ResponsiblePartyLookup',
            element: 'lookupConfigDescription',
          }}
        />
      ) : (
        <Tooltip title={tooltipTitle}>
          <Button
            className={classNames(css(styles.addLookupUserButton), {
              [css(styles.addLookupUserButtonDisabled)]: isButtonDisabled,
            })}
            size={ButtonSize.SMALL}
            buttonStyle={ButtonStyle.SECONDARY_WHITE}
            icon="compose"
            text="Add lookup user"
            onClick={() => {
              runLookupConfigModalForResponsibleParty({
                baseSchema: props.taskSchema,
                firstLookupStepTypes: [LookupStepType.SCHEMA_FIELD],
                intermediateLookupStepTypes: DEFAULT_INTERMEDIATE_LOOKUP_STEP_TYPES,
                finalLookupStepTypes: [LookupStepType.AUTHORIZED_USERS, LookupStepType.SAMPLE_OWNERS],
                onRequestClose: () => {},
                onRequestSubmit: ([{lookupConfig}]) => {
                  props.onChange(lookupConfig);
                },
                submitButtonText: 'Done',
              });
            }}
            dataTestAttrs={{component: 'ResponsiblePartyLookup', element: 'set'}}
            disabled={isButtonDisabled}
          />
        </Tooltip>
      )}
      <ErrorMessage error={props.error ?? null} />
    </>
  );
}

export default function ResponsiblePartyLookupConfigField(
  props: ResponsiblePartyLookupConfigFieldProps
): JSX.Element {
  const componentId = useRef(_.uniqueId());

  return (
    <InputWrapper
      id={componentId.current}
      label={props.label}
      labelStyle={FieldLabel.STYLES.LABEL}
      isRequired={false}
      help={LOOKUP_HELP_TEXT}
    >
      <FormGroup className={classNames('u-no-spacing-bottom')}>
        {props.isLoading ? <HorizontalThrobber /> : <ResponsiblePartyLookupFieldContent {...props} />}
      </FormGroup>
    </InputWrapper>
  );
}

const styles = StyleSheet.create({
  addLookupUserButton: {
    color: BLUE_LIGHT,
    fontWeight: 600,
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
    marginRight: '6px',
    marginLeft: '-9px',
  },
  addLookupUserButtonDisabled: {
    color: GRAY,
  },
});
